import { ChangeEvent, useContext, useEffect, useState } from 'react';
import HeaderPendingAds from './HeaderPendingAds.tsx';
import CardPendingAds from './CardPendingAds.tsx';
import axios from 'axios';
import IResponse, { IPagination } from '../../interfaces/response.ts';
import { IAd, IMyEntities } from '../../interfaces/tasks.ts';
import { MAX_ADS_PAGE_LIMIT } from '../../constants/numbers.ts';
import { Box, Card, CircularProgress, Pagination, Typography } from '@mui/material';
import { CampaignContext } from '../../contexts/CampaignContext.tsx';
import { useTranslation } from 'react-i18next';

export default function PendingAds() {
	const { t } = useTranslation();

	const [fetchedData, setFetchedData] = useState<IAd[]>([]);
	const [fetchedAccounts, setFetchedAccounts] = useState<IMyEntities[]>([]);
	const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);

	const totalItems = pagination?.total;

	const { selectedContentType, selectedPlatformId, selectedCategoryId } = useContext(CampaignContext);

	const fetchAds = async () => {
		try {
			setLoading(true);
			const response = await axios<IResponse<IAd[]>>({
				url: '/duties',
				params: {
					pending: 1,
					page: page,
					limit: MAX_ADS_PAGE_LIMIT,
					category_id: selectedCategoryId,
					media_id: selectedPlatformId,
					content_type: selectedContentType,
					account_id: selectedAccountId,
				},
			});

			if (response.data.success) {
				setPagination(response.data.pagination);
				setFetchedData(response.data.data);
			}
		} catch (error) {
			console.error('Failed to fetch ads', error);
		} finally {
			setLoading(false);
		}
	};

	const handleChangePage = (_: ChangeEvent<unknown>, newPage: number) => {
		setPage(newPage);
	};

	useEffect(() => {
		fetchAds();
	}, [page, selectedAccountId, selectedContentType, selectedPlatformId, selectedCategoryId]);

	useEffect(() => {
		setLoading(true);
		axios<IResponse<IMyEntities[]>>({
			url: '/media_entities/my',
		})
			.then(response => {
				if (response.data.success) {
					setFetchedAccounts(response.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, []);

	return (
		<div className="flex h-full w-full flex-col">
			<div className={'w-full'}>
				<HeaderPendingAds fetchedAccounts={fetchedAccounts} selectedAccountId={selectedAccountId} setSelectedAccountId={setSelectedAccountId} />
				<Card className={'mt-4 flex w-full flex-col gap-3 pb-4'}>
					{loading ? (
						<Box className="flex min-h-96 w-full items-center justify-center">
							<CircularProgress size={30} />
						</Box>
					) : (
						<>
							{fetchedData?.length > 0 ? (
								<div className="flex w-full flex-wrap">{fetchedData?.map((card, i) => <CardPendingAds refetch={fetchAds} card={card} key={i} />)}</div>
							) : (
								<Box className={'py-6 text-center'}>
									<Typography className={'text-base text-text-disabled'}>{t('no_result')}</Typography>
								</Box>
							)}
							<Pagination count={Math.ceil(totalItems! / MAX_ADS_PAGE_LIMIT)} page={page} onChange={handleChangePage} variant="text" showFirstButton={false} showLastButton={false} className="flex justify-center" size="medium" shape="rounded" color="primary" />
						</>
					)}
				</Card>
			</div>
		</div>
	);
}
